import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Row, Col, Card, CardBody, Button, Badge, Table,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, FormFeedback,
} from 'reactstrap';
import { RefreshCw, Plus, Edit2, Users, Eye, EyeOff } from 'react-feather';
import { Breadcrumbs } from '../../AbstractElements';
import api from '../../api/prospect';

// ─── constantes ────────────────────────────────────────────────────────────────

const STATUS_OPTS = [
  { value: 'active',    label: 'Ativo' },
  { value: 'suspended', label: 'Suspenso' },
  { value: 'inactive',  label: 'Inativo' },
];

const UF_OPTS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA',
  'MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN',
  'RS','RO','RR','SC','SP','SE','TO',
];

const EMPTY_CREATE = {
  razao_social: '', nome_fantasia: '', cnpj: '', telefone: '',
  endereco: '', cidade: '', uf: '',
  responsavel_nome: '', responsavel_whatsapp: '',
  email: '', password: '', max_connections: 1,
};
const EMPTY_EDIT = {
  razao_social: '', nome_fantasia: '', cnpj: '', telefone: '',
  endereco: '', cidade: '', uf: '',
  responsavel_nome: '', responsavel_whatsapp: '',
  email: '', max_connections: 1, status: 'active',
};

const PH = { color: '#bbb' };

// ─── máscaras puro JS ──────────────────────────────────────────────────────────

function maskCNPJ(v) {
  const d = v.replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function maskPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10)
    return d.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').replace(/-$/, '');
  return d.replace(/^(\d{2})(\d{1})(\d{4})(\d{0,4})/, '($1) $2 $3-$4').replace(/-$/, '');
}

// ─── componente principal ──────────────────────────────────────────────────────

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen,   setEditOpen]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editForm,   setEditForm]   = useState(EMPTY_EDIT);
  const [errors,     setErrors]     = useState({});

  const [showPwd, setShowPwd] = useState(false);

  const razaoRef     = useRef(null);
  const razaoEditRef = useRef(null);

  const fetchTenants = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/tenants');
      setTenants(data.tenants ?? data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  useEffect(() => {
    if (createOpen) setTimeout(() => razaoRef.current?.focus(), 100);
  }, [createOpen]);

  useEffect(() => {
    if (editOpen) setTimeout(() => razaoEditRef.current?.focus(), 100);
  }, [editOpen]);

  // ── helpers form ──────────────────────────────────────────────────────────────

  function setC(field) {
    return e => setCreateForm(f => ({ ...f, [field]: e.target.value }));
  }
  function setCMasked(field, maskFn) {
    return e => setCreateForm(f => ({ ...f, [field]: maskFn(e.target.value) }));
  }
  function setE(field) {
    return e => setEditForm(f => ({ ...f, [field]: e.target.value }));
  }
  function setEMasked(field, maskFn) {
    return e => setEditForm(f => ({ ...f, [field]: maskFn(e.target.value) }));
  }

  // ── create ────────────────────────────────────────────────────────────────────

  function openCreate() {
    setCreateForm(EMPTY_CREATE);
    setErrors({});
    setCreateOpen(true);
  }

  function validateCreate() {
    const e = {};
    const req = (k, label) => { if (!createForm[k]?.trim()) e[k] = `${label} obrigatório`; };
    req('razao_social',         'Razão social');
    req('nome_fantasia',        'Nome fantasia');
    req('cnpj',                 'CNPJ');
    req('responsavel_nome',     'Nome do responsável');
    req('responsavel_whatsapp', 'WhatsApp do responsável');
    req('email',                'E-mail');
    req('password',             'Senha');
    if (!createForm.max_connections || Number(createForm.max_connections) < 1)
      e.max_connections = 'Mínimo 1';
    return e;
  }

  async function handleCreate(ev) {
    ev.preventDefault();
    const errs = validateCreate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.post('/admin/tenants', {
        name:            createForm.razao_social,
        email:           createForm.email,
        password:        createForm.password,
        max_connections: Number(createForm.max_connections),
        metadata: {
          nome_fantasia:        createForm.nome_fantasia,
          cnpj:                 createForm.cnpj,
          telefone:             createForm.telefone,
          endereco:             createForm.endereco,
          cidade:               createForm.cidade,
          uf:                   createForm.uf,
          responsavel_nome:     createForm.responsavel_nome,
          responsavel_whatsapp: createForm.responsavel_whatsapp,
        },
      });
      setCreateOpen(false);
      await fetchTenants();
    } catch (err) {
      setErrors({ api: err?.response?.data?.message ?? 'Erro ao criar tenant' });
    } finally { setSaving(false); }
  }

  // ── edit ──────────────────────────────────────────────────────────────────────

  function openEdit(tenant) {
    setEditTarget(tenant);
    const m = tenant.metadata ?? {};
    setEditForm({
      razao_social:         m.razao_social         ?? tenant.name ?? '',
      nome_fantasia:        m.nome_fantasia         ?? '',
      cnpj:                 m.cnpj                 ?? '',
      telefone:             m.telefone              ?? '',
      endereco:             m.endereco              ?? '',
      cidade:               m.cidade               ?? '',
      uf:                   m.uf                   ?? '',
      responsavel_nome:     m.responsavel_nome      ?? '',
      responsavel_whatsapp: m.responsavel_whatsapp  ?? '',
      email:                tenant.email            ?? '',
      max_connections:      tenant.max_connections  ?? 1,
      status:               tenant.status           ?? 'active',
    });
    setErrors({});
    setEditOpen(true);
  }

  async function handleEdit(ev) {
    ev.preventDefault();
    if (!editForm.max_connections || Number(editForm.max_connections) < 1) {
      setErrors({ max_connections: 'Mínimo 1' }); return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/tenants/${editTarget.id}`, {
        name:            editForm.razao_social || editTarget.name,
        max_connections: Number(editForm.max_connections),
        status:          editForm.status,
        metadata: {
          razao_social:         editForm.razao_social,
          nome_fantasia:        editForm.nome_fantasia,
          cnpj:                 editForm.cnpj,
          telefone:             editForm.telefone,
          endereco:             editForm.endereco,
          cidade:               editForm.cidade,
          uf:                   editForm.uf,
          responsavel_nome:     editForm.responsavel_nome,
          responsavel_whatsapp: editForm.responsavel_whatsapp,
        },
      });
      setEditOpen(false);
      await fetchTenants();
    } catch (err) {
      setErrors({ api: err?.response?.data?.message ?? 'Erro ao salvar' });
    } finally { setSaving(false); }
  }

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrumbs mainTitle='Tenants' parent='Admin' title='Tenants' />
      <Container fluid>

        {/* Header */}
        <div className='d-flex align-items-center justify-content-between mb-4'>
          <div>
            <h4 className='mb-0 fw-bold'>Tenants</h4>
            <p className='text-muted small mb-0'>Gerencie os tenants da plataforma</p>
          </div>
          <div className='d-flex gap-2'>
            <Button color='light' size='sm' onClick={fetchTenants} disabled={loading}>
              <RefreshCw size={13} className='me-1' /> Atualizar
            </Button>
            <Button color='primary' size='sm' onClick={openCreate}>
              <Plus size={13} className='me-1' /> Novo Tenant
            </Button>
          </div>
        </div>

        {/* Stat */}
        <Row className='mb-4'>
          <Col md={3}>
            <Card>
              <CardBody className='d-flex align-items-center gap-3'>
                <div className='rounded-2 bg-light text-purple d-flex align-items-center justify-content-center flex-shrink-0'
                     style={{ width: 40, height: 40 }}>
                  <Users size={18} />
                </div>
                <div>
                  <p className='text-muted text-uppercase mb-0' style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>Total de tenants</p>
                  <p className='h5 fw-bold mb-0 text-purple'>{tenants.length}</p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Tabela */}
        <Card>
          <CardBody className='p-0'>
            <div className='px-4 py-3 border-bottom'>
              <p className='mb-0 fw-semibold small'>Lista de Tenants</p>
            </div>
            {loading ? (
              <div className='d-flex align-items-center justify-content-center p-5 text-muted small'>
                <RefreshCw size={16} className='me-2' style={{ animation: 'spin 1s linear infinite' }} /> Carregando...
              </div>
            ) : tenants.length === 0 ? (
              <div className='p-5 text-center text-muted small'>Nenhum tenant cadastrado</div>
            ) : (
              <div className='table-responsive'>
                <Table hover className='mb-0'>
                  <thead className='table-light'>
                    <tr className='small text-muted text-uppercase'>
                      <th>Nome</th>
                      <th>E-mail</th>
                      <th className='text-center'>Status</th>
                      <th className='text-center'>Máx. Conexões</th>
                      <th className='text-center'>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map(t => (
                      <tr key={t.id}>
                        <td className='fw-medium small'>{t.nome_fantasia || t.name}</td>
                        <td className='small text-muted'>{t.email}</td>
                        <td className='text-center'>
                          <Badge color={
                            t.status === 'active'    ? 'success' :
                            t.status === 'suspended' ? 'danger'  : 'secondary'
                          }>
                            {STATUS_OPTS.find(s => s.value === t.status)?.label ?? t.status}
                          </Badge>
                        </td>
                        <td className='text-center small'>{t.max_connections ?? '—'}</td>
                        <td className='text-center'>
                          <Button color='light' size='sm' onClick={() => openEdit(t)} title='Editar'>
                            <Edit2 size={13} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* ── Modal Criar ── */}
      <Modal isOpen={createOpen} toggle={() => setCreateOpen(false)} centered size='lg'>
        <ModalHeader toggle={() => setCreateOpen(false)}>Novo Tenant</ModalHeader>
        <Form onSubmit={handleCreate}>
          <ModalBody>
            {errors.api && <div className='alert alert-danger small py-2'>{errors.api}</div>}

            {/* ── Seção 1: Dados da empresa ── */}
            <p className='fw-semibold small text-muted text-uppercase mb-3' style={{ letterSpacing: '0.05em' }}>
              Dados da empresa
            </p>

            <FormGroup>
              <Label className='small fw-semibold'>Razão Social *</Label>
              <Input
                innerRef={razaoRef}
                value={createForm.razao_social}
                onChange={setC('razao_social')}
                invalid={!!errors.razao_social}
                placeholder='Razão social completa'
                style={{ '::placeholder': PH }}
              />
              <FormFeedback>{errors.razao_social}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label className='small fw-semibold'>Nome Fantasia *</Label>
              <Input
                value={createForm.nome_fantasia}
                onChange={setC('nome_fantasia')}
                invalid={!!errors.nome_fantasia}
                placeholder='Nome fantasia'
              />
              <FormFeedback>{errors.nome_fantasia}</FormFeedback>
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className='small fw-semibold'>CNPJ *</Label>
                  <Input
                    value={createForm.cnpj}
                    onChange={setCMasked('cnpj', maskCNPJ)}
                    invalid={!!errors.cnpj}
                    placeholder='00.000.000/0000-00'
                    maxLength={18}
                  />
                  <FormFeedback>{errors.cnpj}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className='small fw-semibold'>Telefone</Label>
                  <Input
                    value={createForm.telefone}
                    onChange={setCMasked('telefone', maskPhone)}
                    placeholder='(00) 0 0000-0000'
                    maxLength={16}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label className='small fw-semibold'>Endereço</Label>
              <Input
                value={createForm.endereco}
                onChange={setC('endereco')}
                placeholder='Logradouro, número, complemento'
              />
            </FormGroup>

            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label className='small fw-semibold'>Cidade</Label>
                  <Input
                    value={createForm.cidade}
                    onChange={setC('cidade')}
                    placeholder='Cidade'
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className='small fw-semibold'>UF</Label>
                  <Input
                    type='select'
                    value={createForm.uf}
                    onChange={setC('uf')}
                  >
                    <option value=''>—</option>
                    {UF_OPTS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <hr className='my-3' />

            {/* ── Seção 2: Responsável ── */}
            <p className='fw-semibold small text-muted text-uppercase mb-3' style={{ letterSpacing: '0.05em' }}>
              Responsável
            </p>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className='small fw-semibold'>Nome *</Label>
                  <Input
                    value={createForm.responsavel_nome}
                    onChange={setC('responsavel_nome')}
                    invalid={!!errors.responsavel_nome}
                    placeholder='Nome completo'
                  />
                  <FormFeedback>{errors.responsavel_nome}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className='small fw-semibold'>WhatsApp *</Label>
                  <Input
                    value={createForm.responsavel_whatsapp}
                    onChange={setCMasked('responsavel_whatsapp', maskPhone)}
                    invalid={!!errors.responsavel_whatsapp}
                    placeholder='(00) 0 0000-0000'
                    maxLength={16}
                  />
                  <FormFeedback>{errors.responsavel_whatsapp}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <hr className='my-3' />

            {/* ── Seção 3: Dados de login ── */}
            <p className='fw-semibold small text-muted text-uppercase mb-3' style={{ letterSpacing: '0.05em' }}>
              Dados de login
            </p>

            <FormGroup>
              <Label className='small fw-semibold'>E-mail *</Label>
              <Input
                type='email'
                value={createForm.email}
                onChange={setC('email')}
                invalid={!!errors.email}
                placeholder='admin@empresa.com'
                autoComplete='new-password'
              />
              <FormFeedback>{errors.email}</FormFeedback>
            </FormGroup>

            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label className='small fw-semibold'>Senha *</Label>
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    border: `1px solid ${errors.password ? '#dc3545' : '#dee2e6'}`,
                    borderRadius: 6, overflow: 'hidden',
                  }}>
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={createForm.password}
                      onChange={setC('password')}
                      placeholder='Senha inicial'
                      autoComplete='new-password'
                      style={{ flex: 1, border: 'none', outline: 'none', padding: '7px 12px', fontSize: 14 }}
                    />
                    <button
                      type='button'
                      tabIndex={-1}
                      onClick={() => setShowPwd(v => !v)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: '0 10px', display: 'flex' }}
                    >
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {errors.password && <div style={{ color: '#dc3545', fontSize: '0.875em', marginTop: 4 }}>{errors.password}</div>}
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className='small fw-semibold'>Máx. Conexões *</Label>
                  <Input
                    type='number'
                    min={1}
                    value={createForm.max_connections}
                    onChange={setC('max_connections')}
                    invalid={!!errors.max_connections}
                  />
                  <FormFeedback>{errors.max_connections}</FormFeedback>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button color='light' onClick={() => setCreateOpen(false)} disabled={saving}>Cancelar</Button>
            <Button color='primary' type='submit' disabled={saving}>
              {saving ? 'Salvando…' : 'Criar Tenant'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* ── Modal Editar ── */}
      <Modal isOpen={editOpen} toggle={() => setEditOpen(false)} centered size='lg'>
        <ModalHeader toggle={() => setEditOpen(false)}>
          Editar Tenant — {editForm.nome_fantasia || editForm.razao_social || editTarget?.name}
        </ModalHeader>
        <Form onSubmit={handleEdit}>
          <ModalBody>
            {errors.api && <div className='alert alert-danger small py-2'>{errors.api}</div>}

            {/* ── Seção 1: Dados da empresa ── */}
            <p className='fw-semibold small text-muted text-uppercase mb-3' style={{ letterSpacing: '0.05em' }}>
              Dados da empresa
            </p>

            <FormGroup>
              <Label className='small fw-semibold'>Razão Social</Label>
              <Input
                innerRef={razaoEditRef}
                value={editForm.razao_social}
                onChange={setE('razao_social')}
                placeholder='Razão social completa'
              />
            </FormGroup>

            <FormGroup>
              <Label className='small fw-semibold'>Nome Fantasia</Label>
              <Input
                value={editForm.nome_fantasia}
                onChange={setE('nome_fantasia')}
                placeholder='Nome fantasia'
              />
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className='small fw-semibold'>CNPJ</Label>
                  <Input
                    value={editForm.cnpj}
                    onChange={setEMasked('cnpj', maskCNPJ)}
                    placeholder='00.000.000/0000-00'
                    maxLength={18}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className='small fw-semibold'>Telefone</Label>
                  <Input
                    value={editForm.telefone}
                    onChange={setEMasked('telefone', maskPhone)}
                    placeholder='(00) 0 0000-0000'
                    maxLength={16}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label className='small fw-semibold'>Endereço</Label>
              <Input
                value={editForm.endereco}
                onChange={setE('endereco')}
                placeholder='Logradouro, número, complemento'
              />
            </FormGroup>

            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label className='small fw-semibold'>Cidade</Label>
                  <Input
                    value={editForm.cidade}
                    onChange={setE('cidade')}
                    placeholder='Cidade'
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className='small fw-semibold'>UF</Label>
                  <Input
                    type='select'
                    value={editForm.uf}
                    onChange={setE('uf')}
                  >
                    <option value=''>—</option>
                    {UF_OPTS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <hr className='my-3' />

            {/* ── Seção 2: Responsável ── */}
            <p className='fw-semibold small text-muted text-uppercase mb-3' style={{ letterSpacing: '0.05em' }}>
              Responsável
            </p>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className='small fw-semibold'>Nome</Label>
                  <Input
                    value={editForm.responsavel_nome}
                    onChange={setE('responsavel_nome')}
                    placeholder='Nome completo'
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className='small fw-semibold'>WhatsApp</Label>
                  <Input
                    value={editForm.responsavel_whatsapp}
                    onChange={setEMasked('responsavel_whatsapp', maskPhone)}
                    placeholder='(00) 0 0000-0000'
                    maxLength={16}
                  />
                </FormGroup>
              </Col>
            </Row>

            <hr className='my-3' />

            {/* ── Seção 3: Dados de login ── */}
            <p className='fw-semibold small text-muted text-uppercase mb-3' style={{ letterSpacing: '0.05em' }}>
              Dados de login
            </p>

            <FormGroup>
              <Label className='small fw-semibold'>E-mail</Label>
              <Input
                type='email'
                value={editForm.email}
                readOnly
                className='bg-light'
              />
            </FormGroup>

            <Row>
              <Col md={8}>
                <FormGroup>
                  <Label className='small fw-semibold'>Máx. Conexões</Label>
                  <Input
                    type='number'
                    min={1}
                    value={editForm.max_connections}
                    onChange={setE('max_connections')}
                    invalid={!!errors.max_connections}
                  />
                  <FormFeedback>{errors.max_connections}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label className='small fw-semibold'>Status</Label>
                  <Input
                    type='select'
                    value={editForm.status}
                    onChange={setE('status')}
                  >
                    {STATUS_OPTS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color='light' onClick={() => setEditOpen(false)} disabled={saving}>Cancelar</Button>
            <Button color='primary' type='submit' disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
}
